import { useStore } from '../store/useStore';
import { useState, useEffect } from 'react';

export const StatusBar = () => {
  const activeCompany = useStore((state) => state.activeCompany);
  const licenseInfo   = useStore((state) => state.licenseInfo);
  const [time, setTime] = useState(() => _fmtTime(new Date()));
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const id = setInterval(() => setTime(_fmtTime(new Date())), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const getPlanBadge = () => {
    if (!licenseInfo) return { text: 'NO LICENSE', color: '#ef4444' };
    if (licenseInfo.type === 'demo') {
      const days = Math.ceil(
        (new Date(licenseInfo.expiresAt!).getTime() - Date.now()) / 86400000
      );
      return { text: `TRIAL  ${days}d left`, color: '#b45309' };
    }
    const planColors: Record<string, string> = {
      basic: '#4b7cf3', pro: '#1e8585', unlimited: '#7c3aed',
    };
    return {
      text: licenseInfo.plan.toUpperCase(),
      color: planColors[licenseInfo.plan] || '#1e8585',
    };
  };

  const badge = getPlanBadge();

  const fmtPeriod = (d: string) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${day}-${months[parseInt(m)-1]}-${y}`;
  };

  return (
    <div
      className="bg-[#1a7474] text-white flex items-center justify-between px-3 shrink-0"
      style={{ height: '22px', fontSize: '10.5px', fontWeight: 500, letterSpacing: '0.2px' }}
    >
      {/* Left — plan + company */}
      <div className="flex items-center gap-3">
        {/* Plan badge */}
        <span
          style={{
            background: badge.color, color: 'white',
            padding: '1px 7px', borderRadius: '2px',
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px',
          }}
        >
          {badge.text}
        </span>

        {/* Company name */}
        {activeCompany && (
          <>
            <span style={{ opacity: 0.5 }}>|</span>
            <span style={{ fontWeight: 600 }}>{activeCompany.name}</span>
          </>
        )}

        {/* License expiry — financial year nahi, license validity dikhao */}
        {licenseInfo?.type === 'full' && (
          <>
            <span style={{ opacity: 0.5 }}>|</span>
            <span style={{ opacity: 0.85 }}>
              {licenseInfo.expiresAt
                ? (() => {
                    const daysLeft = Math.ceil(
                      (new Date(licenseInfo.expiresAt).getTime() - Date.now()) / 86400000
                    );
                    const expStr = fmtPeriod(licenseInfo.expiresAt.split('T')[0]);
                    return daysLeft <= 30
                      ? <span style={{ color: daysLeft <= 7 ? '#fca5a5' : '#fcd34d' }}>
                          Expires {expStr} ({daysLeft}d left)
                        </span>
                      : <span>Valid till {expStr}</span>;
                  })()
                : <span style={{ color: '#4ade80' }}>Lifetime</span>
              }
            </span>
          </>
        )}
      </div>

      {/* Center — shortcut hints */}
      {activeCompany && (
        <div className="flex items-center gap-1.5" style={{ opacity: 0.5, fontSize: '9.5px' }}>
          <span>F1: Help</span>
          <span>·</span>
          <span>F2: Date</span>
          <span>·</span>
          <span>F3: Company</span>
          <span>·</span>
          <span>F4-F9: Vouchers</span>
          <span>·</span>
          <span>Ctrl+/: Search</span>
          <span>·</span>
          <span>Esc: Back</span>
        </div>
      )}

      {/* Right — network + time */}
      <div className="flex items-center gap-3">
        {/* Network dot */}
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', opacity: 0.9 }}>
          <span
            style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: isOnline ? '#4ade80' : '#ef4444',
              display: 'inline-block', flexShrink: 0,
            }}
          />
          <span style={{ fontSize: '10px', opacity: 0.8 }}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </span>

        <span style={{ opacity: 0.5 }}>|</span>

        {/* Clock */}
        <span style={{ fontFamily: "'JetBrains Mono', 'Courier New', monospace", letterSpacing: '0.5px' }}>
          {time}
        </span>
      </div>
    </div>
  );
};

function _fmtTime(d: Date): string {
  const h  = String(d.getHours()).padStart(2, '0');
  const m  = String(d.getMinutes()).padStart(2, '0');
  const s  = String(d.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}
